import { ReactQuery } from 'cms-api'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import { formatPrice } from 'shared-constants'
import { AddToFavorites } from 'components/AddToFavorites'
import { Box, Flex } from 'components/Layout'
import { H6, Text } from 'components/Typography'
import { ASPECT_RATIO } from 'components/UploadImage'
import { useUserAuth } from 'providers/userAuth'
import { COLOR_INTENT, FONT_SIZE, FONT_STACK, FONT_WEIGHT, SPACE } from 'Theme'
import { getRangeDiscountBadge } from 'utils/formatters'

export type ProductThumbnailListProps = {
  variantId: number
  productImageUrl: string
  productImageAlt: string
  title: string
  price: number
  tradePrice: number
  url: string
  options?: string[]
  onRefetch: () => void
}

export const ProductThumbnailList = ({
  variantId,
  productImageUrl,
  productImageAlt,
  title,
  price,
  tradePrice,
  url,
  options,
  onRefetch,
}: ProductThumbnailListProps) => {
  const { user, isClientSignedIn, isExpertSignedIn } = useUserAuth()

  const {
    data: favoriteUserProductVariant,
    refetch: refetchFavoriteUserProductVariant,
  } = ReactQuery.useGetFavoriteUserProductVariantBookmarkLegacyProductVariantId(
    variantId?.toString(),
    {
      query: {
        enabled: isClientSignedIn || isExpertSignedIn,
      },
    }
  )

  const shouldDisplayTradePrice = user?.isTradeCapable && tradePrice

  const discountBadge = getRangeDiscountBadge({
    priceMin: price,
    priceMax: price,
    tradePriceMin: tradePrice,
    tradePriceMax: tradePrice,
  })

  const isBookmarked = !!favoriteUserProductVariant?.data?.isBookmarked

  return (
    <Box>
      <Link href={url} passHref>
        <Flex
          p={{ MOBILE: SPACE.PX_16, TABLET: SPACE.PX_32 }}
          alignItems="center"
          bg="white"
          borderTop="1px solid #E1E1E1"
        >
          <Box
            width={{ MOBILE: '60px', TABLET: '84px' }}
            borderRadius="10px"
            overflow="hidden"
          >
            <Image
              alt={productImageAlt || title}
              objectFit="cover"
              style={{
                aspectRatio: ASPECT_RATIO.PRODUCTS,
                width: '100%',
              }}
              src={productImageUrl}
              sizes="100vw"
              width="0"
              height="0"
            />
          </Box>

          <Box width="40%" ml={SPACE.PX_24}>
            <H6
              mb={0}
              color={COLOR_INTENT.COLLECTION_WIDGET_CARD.TITLE}
              fontSize={{
                MOBILE: FONT_SIZE.PX_14,
                TABLET: FONT_SIZE.PX_16,
              }}
            >
              {title}
            </H6>
            {options &&
              options.length > 0 &&
              options.map((option) => (
                <Text
                  key={option}
                  fontSize={FONT_SIZE.PX_12}
                  color={COLOR_INTENT.COLLECTION_WIDGET_CARD.TITLE}
                  as="p"
                >
                  {option}
                </Text>
              ))}
          </Box>

          <Flex mt={SPACE.PX_12} flexDirection="column" width="30%">
            {shouldDisplayTradePrice && (
              <>
                <Text
                  color={COLOR_INTENT.COLLECTION_WIDGET_CARD.TRADE_PRICE}
                  mr={SPACE.PX_12}
                  mb={SPACE.PX_4}
                  fontSize={{
                    MOBILE: FONT_SIZE.PX_13,
                    TABLET: FONT_SIZE.PX_14,
                  }}
                  fw={FONT_WEIGHT.MEDIUM}
                >
                  Trade: {formatPrice({ price: tradePrice })}{' '}
                </Text>
                {shouldDisplayTradePrice && discountBadge > 0 && (
                  <Text
                    fontSize={{
                      MOBILE: FONT_SIZE.PX_13,
                      TABLET: FONT_SIZE.PX_14,
                    }}
                    fontStyle="italic"
                    mb={SPACE.PX_4}
                    fontWeight={FONT_WEIGHT.NORMAL}
                    color={COLOR_INTENT.COLLECTION_WIDGET_CARD.TRADE_PRICE}
                  >{`${discountBadge}% off`}</Text>
                )}
              </>
            )}
            <Text
              fontSize={
                shouldDisplayTradePrice
                  ? FONT_SIZE.PX_12
                  : {
                      MOBILE: FONT_SIZE.PX_13,
                      TABLET: FONT_SIZE.PX_14,
                    }
              }
              fontFamily={FONT_STACK.SANS}
              color={COLOR_INTENT.COLLECTION_WIDGET_CARD.PRICE}
              // isSoldOut={availability === PRODUCT_AVAILABILITY.SOLD_OUT}
              // hasLeadTime={hasLeadTime}
              style={{
                textDecoration: shouldDisplayTradePrice
                  ? 'line-through'
                  : 'none',
              }}
            >
              {formatPrice({ price })}
            </Text>
          </Flex>
          <Flex width="20%" justifyContent="flex-end">
            <AddToFavorites
              productTitle={title}
              variantId={variantId}
              isBookmarked={isBookmarked}
              onRefetch={() => {
                refetchFavoriteUserProductVariant()
                onRefetch?.()
              }}
            />
          </Flex>
        </Flex>
      </Link>
    </Box>
  )
}
